import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSite, resolveImageUrl } from "../context/SiteContext";

const links = [
  { label: "Services", href: "/#services" },
  { label: "Approach", href: "/#approach" },
  { label: "Therapists", href: "/#team" },
  { label: "Spa", href: "/spa" },
  { label: "Visit", href: "/#visit" },
  { label: "FAQ", href: "/#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { site } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const brand = site?.brand_name || "Let Go Wellness";
  const tagline = site?.brand_tagline || "wellness";
  // split brand name for primary + secondary words
  const brandParts = brand.split(" ");
  const primaryWord = brandParts.slice(0, brandParts.length > 2 ? 2 : 1).join(" ");
  const restWord = brandParts.slice(brandParts.length > 2 ? 2 : 1).join(" ");

  return (
    <header
      data-testid="site-nav"
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E2DCD0]" : "bg-transparent"
      }`}
      style={{ top: "var(--music-banner-h, 0px)" }}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-10 lg:gap-16 transition-all duration-500 ${scrolled ? "h-28" : "h-44 md:h-52"}`}>
        <a href="/#top" data-testid="brand-logo" className="flex items-center gap-3 group flex-shrink min-w-0">
          {site?.brand_logo_url && (
            <img
              src={resolveImageUrl(site.brand_logo_url)}
              alt={brand}
              data-testid="brand-logo-image"
              className={`transition-all duration-500 object-contain flex-shrink-0 ${scrolled ? "h-20 md:h-24" : "h-28 md:h-36 lg:h-40"}`}
            />
          )}
          <span className={`font-serif font-light text-[#1C1917] group-hover:text-[#3A5A40] transition-all duration-500 leading-none whitespace-nowrap tracking-tight ${scrolled ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"}`}>
            {primaryWord}
          </span>
          {(restWord || tagline) && (
            <>
              <span className={`hidden lg:block h-px bg-[#9F5C45] transition-all duration-500 flex-shrink-0 ${scrolled ? "w-5" : "w-8"}`} />
              <span className={`hidden lg:block font-serif italic text-[#3A5A40] transition-all duration-500 leading-none whitespace-nowrap ${scrolled ? "text-xl" : "text-2xl xl:text-3xl"}`}>
                {restWord || tagline}
              </span>
            </>
          )}
        </a>

        <NavRight links={links} onMobileToggle={() => setOpen((v) => !v)} open={open} />
      </div>

      {open && (
        <div className="md:hidden bg-[#FDFBF7] border-t border-[#E2DCD0]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="text-base text-[#1C1917]">
                {l.label}
              </a>
            ))}
            <a
              href="/#enroll"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-enroll"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 btn-primary text-sm tracking-wide mt-2"
            >
              New Patient <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}


// Reusable right-side nav (menu + CTA + mobile toggle)
function NavRight({ links, onMobileToggle, open, centered = false }) {
  return (
    <div className={`flex items-center gap-6 ${centered ? "w-full justify-center" : ""}`}>
      <nav className={`hidden md:flex items-center ${centered ? "gap-6 md:gap-8" : "gap-6 lg:gap-8"}`}>
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            data-testid={`nav-${l.label.toLowerCase()}`}
            className="text-sm uppercase tracking-[0.22em] text-[#1C1917] hover:text-[#3A5A40] transition-colors relative group"
          >
            {l.label}
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#3A5A40] transition-all duration-300 group-hover:w-full" />
          </a>
        ))}
      </nav>

      <a
        href="/#enroll"
        data-testid="nav-cta-enroll"
        className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 btn-primary text-xs tracking-[0.18em] uppercase ml-2"
      >
        New Patient
        <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
      </a>

      <button
        onClick={onMobileToggle}
        aria-label="Toggle menu"
        data-testid="mobile-menu-toggle"
        className="md:hidden p-2 text-[#1C1917]"
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
    </div>
  );
}
